// src/sectors/pilates/settings/tabs/CompanySettingsTab.jsx
import React from 'react';
import MDBox from 'components/MDBox';
import Icon from '@mui/material/Icon';
import Chip from '@mui/material/Chip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';

import { CompanyActivitiesSection } from './company/CompanyActivitiesSection';
import { CompanyPackagesSection } from './company/CompanyPackagesSection';
import { CompanyIdentitySection } from './company/CompanyIdentitySection';
import { CompanyToolsSection } from './company/CompanyToolsSection';
import { companySettingsApi } from 'core/companySettings';

const EMPTY_DRAFT = { name: '', phone: '', email: '' };

// tools defaults
const DEFAULT_CAL_TYPE = 'TAM_SAAT';
const DEFAULT_DAY_START = 8;
const DEFAULT_DAY_END = 22;

export function CompanySettingsTab({ theme: themeProp }) {
  const muiTheme = useMuiTheme();
  const theme = themeProp || muiTheme;

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState(null);

  const [draft, setDraft] = React.useState(EMPTY_DRAFT);
  const [savedDraft, setSavedDraft] = React.useState(EMPTY_DRAFT);

  const [companyEmails, setCompanyEmails] = React.useState([]);
  const [dirtyIdentity, setDirtyIdentity] = React.useState(false);
  const [plans, setPlans] = React.useState([]);

  // ✅ Tools state
  const [toolsEdit, setToolsEdit] = React.useState(false);
  const [savingTools, setSavingTools] = React.useState(false);

  const [calendarType, setCalendarType] = React.useState(DEFAULT_CAL_TYPE);
  const [savedCalendarType, setSavedCalendarType] = React.useState(DEFAULT_CAL_TYPE);

  const [dayStartHour, setDayStartHour] = React.useState(DEFAULT_DAY_START);
  const [dayEndHour, setDayEndHour] = React.useState(DEFAULT_DAY_END);

  const [savedDayStartHour, setSavedDayStartHour] = React.useState(DEFAULT_DAY_START);
  const [savedDayEndHour, setSavedDayEndHour] = React.useState(DEFAULT_DAY_END);

  const [dirtyTools, setDirtyTools] = React.useState(false);

  const patch = (key, value) => {
    setDraft((p) => ({ ...p, [key]: value }));
    setDirtyIdentity(true);
  };

  const loadAll = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const me = await companySettingsApi.getMy();
      const sector = me?.sectorSettings || {};
      const tools = sector?.tools || {};

      // ✅ tools defaults
      const loadedCalendarType = tools?.calendarType || DEFAULT_CAL_TYPE;

      const loadedStart = Number.isFinite(tools?.dayStartHour)
        ? Number(tools.dayStartHour)
        : DEFAULT_DAY_START;

      const loadedEnd = Number.isFinite(tools?.dayEndHour)
        ? Number(tools.dayEndHour)
        : DEFAULT_DAY_END;

      setCalendarType(loadedCalendarType);
      setSavedCalendarType(loadedCalendarType);

      setDayStartHour(loadedStart);
      setSavedDayStartHour(loadedStart);

      setDayEndHour(loadedEnd);
      setSavedDayEndHour(loadedEnd);

      setDirtyTools(false);
      setToolsEdit(false);

      const nextDraft = {
        name: me?.companyName ?? '',
        phone: me?.companyPhone ?? '',
        email: sector?.notifyEmail ?? '',
      };

      setDraft(nextDraft);
      setSavedDraft(nextDraft);
      setDirtyIdentity(false);

      setCompanyEmails(Array.isArray(me?.companyEmails) ? me.companyEmails : []);
      setPlans(Array.isArray(sector?.plans) ? sector.plans : []);
    } catch (e) {
      console.error(e);
      setErr('Ayarlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadAll();
  }, [loadAll]);

  // ✅ dirtyTools: 3 alanı birlikte kıyasla
  React.useEffect(() => {
    const isDirty =
      String(calendarType) !== String(savedCalendarType) ||
      Number(dayStartHour) !== Number(savedDayStartHour) ||
      Number(dayEndHour) !== Number(savedDayEndHour);

    setDirtyTools(isDirty);
  }, [
    calendarType,
    savedCalendarType,
    dayStartHour,
    savedDayStartHour,
    dayEndHour,
    savedDayEndHour,
  ]);

  const resetDraftFromSaved = React.useCallback(() => {
    setDraft(savedDraft);
    setDirtyIdentity(false);
    setErr(null);
  }, [savedDraft]);

  // ✅ Identity save -> PATCH /me/identity
  const saveIdentity = React.useCallback(
    async (payload) => {
      setErr(null);
      try {
        await companySettingsApi.updateMyIdentity({
          companyName: payload?.name ?? '',
          companyPhone: payload?.phone ?? '',
          notifyEmail: payload?.email ?? '',
        });
        await loadAll();
      } catch (e) {
        console.error(e);
        setErr('Kaydetme başarısız.');
        throw e;
      }
    },
    [loadAll]
  );

  // ✅ Tools cancel/save
  const cancelTools = React.useCallback(() => {
    setCalendarType(savedCalendarType);
    setDayStartHour(savedDayStartHour);
    setDayEndHour(savedDayEndHour);

    setDirtyTools(false);
    setToolsEdit(false);
    setErr(null);
  }, [savedCalendarType, savedDayStartHour, savedDayEndHour]);

  const saveTools = React.useCallback(async () => {
    setSavingTools(true);
    setErr(null);
    try {
      // ✅ tek endpoint
      await companySettingsApi.updateTools({
        calendarType: calendarType || DEFAULT_CAL_TYPE,
        dayStartHour: Number(dayStartHour),
        dayEndHour: Number(dayEndHour),
      });

      await loadAll();
    } catch (e) {
      console.error(e);
      setErr('Araçlar kaydedilemedi.');
    } finally {
      setSavingTools(false);
    }
  }, [calendarType, dayStartHour, dayEndHour, loadAll]);

  // ---- UI tokens (senin aynı) ----
  const bg = theme.palette.mode === 'dark' ? '#070A12' : '#ffffff';
  const PAGE_PAD = { xs: 0.75, sm: 0.9, lg: 1.0 };
  const GAP = 1.0;
  const BODY_PAD = 1.0;
  const HEADER_MIN_H = 54;

  const pageBg = { background: bg, borderRadius: '18px', padding: PAGE_PAD };

  const headerGlow =
    theme.palette.mode === 'dark'
      ? `radial-gradient(760px 220px at 12% 0%, ${alpha('#7C3AED', 0.22)}, transparent 62%),
       radial-gradient(760px 220px at 86% 0%, ${alpha('#06B6D4', 0.2)}, transparent 62%)`
      : `radial-gradient(760px 220px at 12% 0%, ${alpha('#7C3AED', 0.16)}, transparent 62%),
       radial-gradient(760px 220px at 86% 0%, ${alpha('#06B6D4', 0.14)}, transparent 62%)`;

  const cardBase = {
    borderRadius: '18px',
    overflow: 'hidden',
    border: `1px solid ${alpha('#000', 0.06)}`,
    background:
      theme.palette.mode === 'dark'
        ? `linear-gradient(180deg, ${alpha('#0B1020', 0.9)}, ${alpha('#070A12', 0.98)})`
        : `linear-gradient(180deg, ${alpha('#ffffff', 0.98)}, ${alpha('#fbfbff', 0.98)})`,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 12px 34px rgba(0,0,0,.45)'
        : '0 10px 28px rgba(15, 23, 42, .09)',
    backgroundImage: headerGlow,
  };

  const panelHeader = (tone = 'violet') => {
    const map = {
      violet: { a: '#7C3AED', b: '#A78BFA' },
      cyan: { a: '#06B6D4', b: '#67E8F9' },
      emerald: { a: '#10B981', b: '#6EE7B7' },
      rose: { a: '#F43F5E', b: '#FDA4AF' },
    };
    const t = map[tone] || map.violet;

    return {
      px: 1.25,
      py: 0.9,
      minHeight: HEADER_MIN_H,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      borderBottom: `1px solid ${alpha('#000', 0.06)}`,
      background:
        theme.palette.mode === 'dark'
          ? `linear-gradient(90deg, ${alpha(t.a, 0.22)}, ${alpha(t.b, 0.08)})`
          : `linear-gradient(90deg, ${alpha(t.a, 0.14)}, ${alpha(t.b, 0.08)})`,
    };
  };

  const chipBase = (colorHex) => ({
    height: 24,
    borderRadius: 999,
    fontWeight: 900,
    bgcolor: alpha(colorHex, 0.12),
    border: `1px solid ${alpha(colorHex, 0.22)}`,
    '& .MuiChip-label': { px: 1.0 },
  });

  const titleStyle = { fontWeight: 900, lineHeight: 1.05, fontSize: 13 };
  const subStyle = { opacity: 0.85, fontSize: 11, lineHeight: 1.2 };

  const leftCardSx = {
    ...cardBase,
    backgroundImage: 'none',
    display: 'flex',
    flexDirection: 'column',
    minHeight: { xs: 'unset', lg: 'calc(100vh - 200px)' },
  };
  const rightCardSx = {
    ...cardBase,
    backgroundImage: 'none',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  };

  const rightColSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: GAP,
    minHeight: { xs: 'unset', lg: 'calc(100vh - 200px)' },
  };
  const topCardMaxH = { xs: 'unset', lg: '50%' };
  const rightBodySx = { p: BODY_PAD, overflowY: 'auto', flex: 1, minHeight: 0 };

  return (
    <MDBox sx={pageBg}>
      <MDBox
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: GAP,
          alignItems: 'stretch',
        }}
      >
        {/* LEFT — Identity */}
        <MDBox sx={leftCardSx}>
          <MDBox sx={panelHeader('violet')}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ color: '#A78BFA', fontSize: 18 }}>store</Icon>
              <MDBox>
                <div style={titleStyle}>İşletme Bilgileri</div>
                <div style={subStyle}>Ad • Telefon • Bildirim E-postası</div>
              </MDBox>
            </MDBox>
            <Chip size="small" label={loading ? '…' : 'Kimlik'} sx={chipBase('#7C3AED')} />
          </MDBox>

          <MDBox sx={{ p: BODY_PAD }}>
            <CompanyIdentitySection
              draft={draft}
              dirty={dirtyIdentity}
              patch={patch}
              loading={loading}
              error={err}
              companyEmails={companyEmails}
              onSave={saveIdentity}
              onResetDraft={resetDraftFromSaved}
            />
          </MDBox>
        </MDBox>

        {/* RIGHT */}
        <MDBox sx={rightColSx}>
          {/* Activities */}
          <MDBox sx={rightCardSx}>
            <MDBox sx={panelHeader('emerald')}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ color: '#34D399', fontSize: 18 }}>fitness_center</Icon>
                <MDBox>
                  <div style={titleStyle}>Aktiviteler</div>
                  <div style={subStyle}>Ekle • Düzenle • Sil</div>
                </MDBox>
              </MDBox>
              <Chip size="small" label="Liste" sx={chipBase('#10B981')} />
            </MDBox>

            <MDBox sx={{ p: BODY_PAD }}>
              <CompanyActivitiesSection theme={theme} loading={loading} />
            </MDBox>
          </MDBox>

          {/* Plans */}
          <MDBox sx={{ ...rightCardSx, maxHeight: topCardMaxH }}>
            <MDBox sx={panelHeader('cyan')}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ color: '#22D3EE', fontSize: 18 }}>redeem</Icon>
                <MDBox>
                  <div style={titleStyle}>Paketler</div>
                  <div style={subStyle}>Oluştur • Düzenle • Pasife al</div>
                </MDBox>
              </MDBox>

              <Chip
                size="small"
                label={loading ? '…' : `${Array.isArray(plans) ? plans.length : 0}`}
                sx={chipBase('#06B6D4')}
              />
            </MDBox>

            <MDBox sx={rightBodySx}>
              <CompanyPackagesSection loading={loading} plans={plans} onReload={loadAll} />
            </MDBox>
          </MDBox>

          {/* ✅ TOOLS */}
          <MDBox sx={rightCardSx}>
            <MDBox sx={panelHeader('rose')}>
              <MDBox display="flex" alignItems="center" gap={1}>
                <Icon sx={{ color: '#FDA4AF', fontSize: 18 }}>build</Icon>
                <MDBox>
                  <div style={titleStyle}>Araçlar</div>
                  <div style={subStyle}>Takvim zaman tipi • Saat aralığı</div>
                </MDBox>
              </MDBox>

              <Chip size="small" label="Basit" sx={chipBase('#F43F5E')} />
            </MDBox>

            <MDBox sx={{ p: BODY_PAD }}>
              <CompanyToolsSection
                loading={loading}
                error={err}
                calendarType={calendarType}
                setCalendarType={setCalendarType}
                dayStartHour={dayStartHour}
                setDayStartHour={setDayStartHour}
                dayEndHour={dayEndHour}
                setDayEndHour={setDayEndHour}
                dirty={dirtyTools}
                editMode={toolsEdit}
                saving={savingTools}
                onEdit={() => setToolsEdit(true)}
                onCancel={cancelTools}
                onSave={saveTools}
              />
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
